// src/features/engine/tasks/osrboi/OSRBOITask.jsx

import { useNavigate, useParams } from "react-router-dom";

import useOSRBOITask from "./hooks/useOSRBOITask";
import useOSRBOITranslator from "./hooks/useOSRBOITranslator";
import { useTaskRuntime } from "../../context/TaskRuntimeContext";
import OSRBOIPlayer from "./components/OSRBOIPlayer";
import OSRBOIForm from "./components/OSRBOIForm";
import OSRBOIFeedback from "./components/OSRBOIFeedback";

export default function OSRBOITask() {

  const t = useOSRBOITranslator();

  const {
    currentSample,
    currentIndex,
    totalSamples,
    progress,
    showFeedback,
    referenceValues,
    submitEvaluation,
    next,
    loading,
    submitting,
    step
  } = useOSRBOITask();

  // =====================================================
  // NAVIGATION
  // =====================================================

  const navigate = useNavigate();
  const { slug } = useParams();
  const {
    backPath,
    programScopedLocale
  } = useTaskRuntime();

  const resolvedBackPath =
    backPath || (slug ? `/app/project/${slug}` : "/app/assigned-work");

  const isProcessing = submitting;

  // =====================================================
  // TRAINING DETECTION
  // =====================================================

  const isTraining =
    step?.countsForCompletion === false;

  // =====================================================
  // HELPERS
  // =====================================================

  const resolveText = (key, fallback = "") => {
    if (!key) return fallback;
    const translated = t(key);
    return (!translated || translated === key)
      ? fallback
      : translated;
  };

  // =====================================================
  // GUARDS
  // =====================================================

  if (loading) return <p>{t("loading")}</p>;

  if (submitting) {
    return (
      <div className="app-module">
        <p>{t("saving_results")}</p>
        <p>{t("please_wait")}</p>
      </div>
    );
  }

  if (!currentSample) return <p>{t("no_samples")}</p>;

  // =====================================================
  // HANDLE NEXT
  // =====================================================

  const handleNext = async () => {
    next();
  };

  // =====================================================
  // UI
  // =====================================================

  return (

    <div
      className="app-module"
      lang={programScopedLocale?.isEnabled
        ? programScopedLocale.contentLang
        : undefined}
    >

      {/* 🔙 BACK BUTTON */}
      <div style={{ marginBottom: 16 }}>
        <button 
          className="app-btn app-btn-secondary"
          onClick={() => navigate(resolvedBackPath)}
          disabled={isProcessing}
        >
          ⬅  {t("back_to_dashboard")}
        </button>
      </div>

      {/* 🧠 STEP HEADER */}
      <div style={{ marginTop: 48, marginBottom: 24 }}>

        <h2 style={{ marginBottom: 8 }}>
          {resolveText(step?.titleKey, step?.title || step?.key)}
        </h2>

        {step?.instructionsKey && (
          <p style={{ fontSize: 14, opacity: 0.7 }}>
            {t(step.instructionsKey)}
          </p>
        )}

      </div>

      {/* 📊 PROGRESS */}
{!isTraining ? (
  <>
    <p>
      {t("progress_fraction", {
        current: currentIndex + 1,
        total: totalSamples
      })}
    </p>
    <progress value={progress} max="100" />
  </>
) : (
  <p style={{ opacity: 0.6 }}>
    {t("responses_count", { count: currentIndex })}
  </p>
)}
      {/* 🔊 AUDIO */}
      <div style={{ display: "flex", justifyContent: "center", marginTop: 24 }}>
        <OSRBOIPlayer sample={currentSample} />
      </div>

      <br />

      {/* 📝 FORM / FEEDBACK */}
      {!showFeedback ? (

        <OSRBOIForm onSubmit={submitEvaluation} />

      ) : (

        <OSRBOIFeedback
          attributeData={referenceValues}
          onNext={handleNext}
          isLastSample={currentIndex + 1 >= totalSamples}
        />

      )}

    </div>
  );
}
